import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calculator, Plus, PencilSimple, Trash, MagnifyingGlass, ArrowUp, ArrowDown, ArrowsDownUp, Export, UploadSimple, DownloadSimple, FileArrowUp, CheckCircle, Warning } from "@phosphor-icons/react";
import { useTableFeatures, exportCSV } from "@/hooks/useTableFeatures";

const fmt = (n) => (!n || n === 0) ? "$0" : `$${Number(n).toLocaleString()}`;

const defaultForm = {
  client_id: "", entity_name: "", ben: "", funding_year: "",
  budget_cap: 0, pre_discount_amount: 0, committed_amount: 0,
  disbursed_amount: 0, remaining_budget: 0, notes: "",
};

export default function C2Budget() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...defaultForm });
  const [yearFilter, setYearFilter] = useState("all");
  const [importOpen, setImportOpen] = useState(false);
  const [importStep, setImportStep] = useState("upload");
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const fileInputRef = useRef(null);

  const { search, setSearch, sortKey, sortDir, toggleSort, processed } = useTableFeatures(items);
  const csvCols = [
    { label: "Entity", key: "entity_name" }, { label: "BEN", key: "ben" }, { label: "FY", key: "funding_year" },
    { label: "Budget Cap", key: "budget_cap" }, { label: "Pre-Discount", key: "pre_discount_amount" },
    { label: "Committed", key: "committed_amount" }, { label: "Disbursed", key: "disbursed_amount" }, { label: "Remaining", key: "remaining_budget" },
  ];
  const SH = ({ k, children }) => (
    <TableHead className="py-3 px-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold cursor-pointer select-none hover:text-[#1A1D20]" onClick={() => toggleSort(k)}>
      <div className="flex items-center gap-1">{children}{sortKey === k ? (sortDir === "asc" ? <ArrowUp size={10} /> : <ArrowDown size={10} />) : <ArrowsDownUp size={10} className="opacity-30" />}</div>
    </TableHead>
  );

  useEffect(() => { load(); loadClients(); }, []);
  const load = async () => { try { const { data } = await api.get("/c2-budgets"); setItems(data); } catch { toast.error("Failed to load"); } };
  const loadClients = async () => { try { const { data } = await api.get("/clients"); setClients(data); } catch {} };

  const save = async () => {
    try {
      const payload = { ...form, remaining_budget: form.budget_cap - form.committed_amount };
      if (editing) { await api.put(`/c2-budgets/${editing}`, payload); toast.success("Updated"); }
      else { await api.post("/c2-budgets", payload); toast.success("Created"); }
      setOpen(false); load();
    } catch { toast.error("Failed to save"); }
  };

  const remove = async (id) => { if (!window.confirm("Delete?")) return; try { await api.delete(`/c2-budgets/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); } };
  const edit = (item) => { setForm({ ...item }); setEditing(item.id); setOpen(true); };
  const openNew = () => { setForm({ ...defaultForm }); setEditing(null); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const selectClient = (id) => { const c = clients.find((cl) => cl.id === id); setForm((f) => ({ ...f, client_id: id, entity_name: c ? c.name : "", ben: c ? c.ben : "" })); };

  const fundingYears = [...new Set(items.map((i) => i.funding_year).filter(Boolean))].sort().reverse();
  const filtered = yearFilter === "all" ? processed : processed.filter((i) => i.funding_year === yearFilter);

  const downloadTemplate = async () => {
    try {
      const response = await api.get("/c2-budgets/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "c2_budget_import_template.csv";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch { toast.error("Failed to download template"); }
  };

  const openImport = () => { setImportOpen(true); setImportStep("upload"); setImportResult(null); setSelectedFile(null); setImporting(false); };

  const syncUsac = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/c2-budgets/sync-usac");
      toast.success(`Synced ${data.upserted} BEN budgets (cycle ${data.cycle})`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "USAC sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) setSelectedFile(file);
  };

  const executeImport = async () => {
    if (!selectedFile) return;
    setImporting(true);
    const formData = new FormData();
    formData.append("file", selectedFile);
    try {
      const { data } = await api.post("/c2-budgets/import", formData);
      setImportResult(data);
      setImportStep("result");
      if (data.imported > 0) { toast.success(`${data.imported} budget entries imported`); load(); }
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); }
  };

  return (
    <div className="p-6" data-testid="c2budget-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B7280] mb-1">Financials</p>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-[#1A1D20]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>C2 Budget Tool</h1>
          <p className="text-sm text-[#6B7280] mt-1">Category 2 budget tracking per entity</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="sync-usac-c2-btn" onClick={syncUsac} disabled={syncing} className="bg-transparent text-[#002FA7] border border-[#002FA7] hover:bg-[#002FA7]/5 rounded-none font-medium px-4 py-2 text-sm flex items-center gap-2 transition-colors disabled:opacity-50">
            <DownloadSimple size={16} weight="bold" /> {syncing ? "Syncing..." : "Sync USAC (FY26-30)"}
          </button>
          <button data-testid="import-c2-btn" onClick={openImport} className="bg-transparent text-[#1A1D20] border border-[#1A1D20] hover:bg-[#F8F9FA] rounded-none font-medium px-4 py-2 text-sm flex items-center gap-2 transition-colors">
            <UploadSimple size={16} weight="bold" /> Import
          </button>
          <button data-testid="add-c2budget-btn" onClick={openNew} className="bg-[#002FA7] text-white hover:bg-[#002480] rounded-none font-medium px-4 py-2 text-sm flex items-center gap-2 transition-colors">
            <Plus size={16} weight="bold" /> Add Budget Entry
          </button>
        </div>
      </div>

      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
          <Input data-testid="search-c2budget" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-none pl-9" />
        </div>
        <Select value={yearFilter} onValueChange={setYearFilter}>
          <SelectTrigger data-testid="filter-c2-year" className="rounded-none w-[140px]"><SelectValue placeholder="All Years" /></SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="all">All Years</SelectItem>
            {fundingYears.map((y) => <SelectItem key={y} value={y}>FY {y}</SelectItem>)}
          </SelectContent>
        </Select>
        <button data-testid="export-c2-btn" onClick={() => exportCSV(filtered, csvCols, "c2_budget.csv")} className="ml-auto flex items-center gap-1.5 bg-transparent text-[#1A1D20] border border-[#1A1D20] hover:bg-[#F8F9FA] rounded-none font-medium px-3 py-2 text-xs transition-colors">
          <Export size={14} /> Export CSV
        </button>
      </div>

      <div className="border border-gray-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow className="data-table-header">
              <SH k="entity_name">Entity</SH>
              <SH k="ben">BEN</SH>
              <SH k="funding_year">FY</SH>
              <SH k="budget_cap">Budget Cap</SH>
              <SH k="pre_discount_amount">Pre-Discount</SH>
              <SH k="committed_amount">Committed</SH>
              <SH k="disbursed_amount">Disbursed</SH>
              <SH k="remaining_budget">Remaining</SH>
              <TableHead className="py-3 px-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">Utilization</TableHead>
              <TableHead className="py-3 px-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold w-[80px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={10} className="text-center py-8 text-[#6B7280] text-sm">No budget entries</TableCell></TableRow>
            ) : filtered.map((item) => {
              const util = item.budget_cap > 0 ? ((item.committed_amount / item.budget_cap) * 100) : 0;
              return (
                <TableRow key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <TableCell className="py-3 px-4 text-[#1A1D20] font-medium">{item.entity_name || "—"}</TableCell>
                  <TableCell className="py-3 px-4 font-mono text-xs">{item.ben || "—"}</TableCell>
                  <TableCell className="py-3 px-4 text-sm">{item.funding_year || "—"}</TableCell>
                  <TableCell className="py-3 px-4 text-sm font-medium">{fmt(item.budget_cap)}</TableCell>
                  <TableCell className="py-3 px-4 text-sm">{fmt(item.pre_discount_amount)}</TableCell>
                  <TableCell className="py-3 px-4 text-sm font-medium text-[#002FA7]">{fmt(item.committed_amount)}</TableCell>
                  <TableCell className="py-3 px-4 text-sm text-[#00B341]">{fmt(item.disbursed_amount)}</TableCell>
                  <TableCell className="py-3 px-4 text-sm font-medium">{fmt(item.remaining_budget)}</TableCell>
                  <TableCell className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1.5 bg-gray-100"><div className="h-full bg-[#002FA7]" style={{ width: `${Math.min(util, 100)}%` }} /></div>
                      <span className="text-xs text-[#6B7280]">{util.toFixed(0)}%</span>
                    </div>
                  </TableCell>
                  <TableCell className="py-3 px-4">
                    <div className="flex items-center gap-1">
                      <button data-testid={`edit-c2-${item.id}`} onClick={() => edit(item)} className="p-1.5 hover:bg-gray-100"><PencilSimple size={14} className="text-[#6B7280]" /></button>
                      <button data-testid={`delete-c2-${item.id}`} onClick={() => remove(item.id)} className="p-1.5 hover:bg-red-50"><Trash size={14} className="text-[#FF2A00]" /></button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{editing ? "Edit" : "Add"} C2 Budget Entry</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Client / Entity</label>
              <Select value={form.client_id} onValueChange={selectClient}>
                <SelectTrigger data-testid="c2-client-select" className="rounded-none"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent className="rounded-none max-h-[200px]">{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">BEN</label>
                <Input data-testid="c2-ben-input" value={form.ben} onChange={(e) => set("ben", e.target.value)} className="rounded-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Funding Year</label>
                <Input data-testid="c2-fy-input" value={form.funding_year} onChange={(e) => set("funding_year", e.target.value)} className="rounded-none" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Budget Cap ($)</label>
                <Input data-testid="c2-cap-input" type="number" value={form.budget_cap} onChange={(e) => set("budget_cap", parseFloat(e.target.value) || 0)} className="rounded-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Pre-Discount Amount ($)</label>
                <Input data-testid="c2-prediscount-input" type="number" value={form.pre_discount_amount} onChange={(e) => set("pre_discount_amount", parseFloat(e.target.value) || 0)} className="rounded-none" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Committed ($)</label>
                <Input data-testid="c2-committed-input" type="number" value={form.committed_amount} onChange={(e) => set("committed_amount", parseFloat(e.target.value) || 0)} className="rounded-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Disbursed ($)</label>
                <Input data-testid="c2-disbursed-input" type="number" value={form.disbursed_amount} onChange={(e) => set("disbursed_amount", parseFloat(e.target.value) || 0)} className="rounded-none" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Notes</label>
              <textarea data-testid="c2-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="w-full border border-gray-300 rounded-none px-3 py-2 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] min-h-[60px]" />
            </div>
          </div>
          <DialogFooter>
            <button data-testid="cancel-c2-btn" onClick={() => setOpen(false)} className="bg-transparent text-[#1A1D20] border border-[#1A1D20] hover:bg-[#F8F9FA] rounded-none font-medium px-4 py-2 text-sm">Cancel</button>
            <button data-testid="save-c2-btn" onClick={save} className="bg-[#002FA7] text-white hover:bg-[#002480] rounded-none font-medium px-4 py-2 text-sm">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="rounded-none sm:max-w-[580px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              <div className="flex items-center gap-2"><FileArrowUp size={20} className="text-[#002FA7]" /> Import C2 Budget Data</div>
            </DialogTitle>
          </DialogHeader>

          {importStep === "upload" && (
            <div className="py-4">
              <div className="mb-6 p-4 bg-[#F8F9FA] border border-gray-200">
                <p className="text-sm text-[#1A1D20] mb-3">Upload a CSV or Excel file with C2 budget data.</p>
                <button data-testid="download-c2-template-btn" onClick={downloadTemplate} className="flex items-center gap-2 text-sm font-medium text-[#002FA7] hover:text-[#002480] transition-colors">
                  <DownloadSimple size={16} weight="bold" /> Download CSV Template
                </button>
              </div>
              <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleFileSelect} className="hidden" />
              <button data-testid="select-c2-file-btn" onClick={() => fileInputRef.current?.click()}
                className="w-full border-2 border-dashed border-gray-300 hover:border-[#002FA7] p-8 text-center transition-colors group">
                <UploadSimple size={32} className="mx-auto text-[#6B7280] group-hover:text-[#002FA7] mb-3 transition-colors" />
                <p className="text-sm font-medium text-[#1A1D20] mb-1">{selectedFile ? selectedFile.name : "Click to select a file"}</p>
                <p className="text-xs text-[#6B7280]">Supports CSV, XLSX, XLS</p>
              </button>
              <div className="mt-4">
                <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-2">Expected Columns</p>
                <div className="flex flex-wrap gap-1.5">
                  {["entity_name", "ben", "funding_year", "budget_cap", "committed_amount", "disbursed_amount", "remaining_budget"].map((col) => (
                    <span key={col} className="inline-block px-2 py-0.5 text-xs border border-gray-200 text-[#6B7280]">{col}</span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {importStep === "result" && importResult && (
            <div className="py-4">
              <div className={`p-4 mb-4 border ${importResult.imported > 0 ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}`}>
                <div className="flex items-center gap-2 mb-2">
                  {importResult.imported > 0 ? <CheckCircle size={20} className="text-green-600" /> : <Warning size={20} className="text-red-600" />}
                  <span className="text-sm font-semibold text-[#1A1D20]">Import Complete</span>
                </div>
                <div className="grid grid-cols-3 gap-4 text-center mt-3">
                  <div><p className="text-2xl font-bold text-green-600">{importResult.imported}</p><p className="text-xs text-[#6B7280]">Imported</p></div>
                  <div><p className="text-2xl font-bold text-amber-600">{importResult.skipped}</p><p className="text-xs text-[#6B7280]">Skipped</p></div>
                  <div><p className="text-2xl font-bold text-[#1A1D20]">{importResult.total_rows}</p><p className="text-xs text-[#6B7280]">Total Rows</p></div>
                </div>
              </div>
              {importResult.errors?.length > 0 && (
                <div className="p-3 border border-amber-200 bg-amber-50">
                  <p className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-1">Errors</p>
                  {importResult.errors.map((err, i) => <p key={i} className="text-xs text-amber-800">{err}</p>)}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {importStep === "upload" && (
              <>
                <button onClick={() => setImportOpen(false)} className="bg-transparent text-[#1A1D20] border border-[#1A1D20] hover:bg-[#F8F9FA] rounded-none font-medium px-4 py-2 text-sm">Cancel</button>
                {selectedFile && (
                  <button data-testid="confirm-c2-import-btn" onClick={executeImport} disabled={importing} className="bg-[#002FA7] text-white hover:bg-[#002480] disabled:opacity-50 rounded-none font-medium px-4 py-2 text-sm">
                    {importing ? "Importing..." : "Import"}
                  </button>
                )}
              </>
            )}
            {importStep === "result" && (
              <button data-testid="close-c2-import-btn" onClick={() => setImportOpen(false)} className="bg-[#002FA7] text-white hover:bg-[#002480] rounded-none font-medium px-4 py-2 text-sm">Done</button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
